import { useEffect, useId, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { TrackLayoutData } from "@/types";
import {
  calculateBounds,
  convertGPSToSVG,
  createPathFromPoints,
} from "@/utils/gpsProjection";
import {
  useTrackMapZoom,
  type TrackMapControls,
} from "@/components/tracks/useTrackMapZoom";
import TeamPositionMarkers from "./TeamPositionMarkers";

interface TrackLayoutVisualizationProps {
  layoutData: TrackLayoutData;
  className?: string;
  showSectors?: boolean;
  showPitlane?: boolean;
  highlightedSector?: number;
  currentPosition?: { lat: number; lng: number };
  currentSpeed?: number; // km/h for color coding
  positionTrail?: Array<{
    lat: number;
    lng: number;
    timestamp: number;
    speed?: number;
  }>;
  /**
   * Live team markers (team live map). Rendered INSTEAD of the single-vehicle
   * currentPosition marker when present — one colored, labeled dot per member.
   */
  teamPositions?: Array<{
    userId: string;
    label: string;
    lat: number;
    lng: number;
    speedKmh?: number | null;
    stale?: boolean;
  }>;
  showSpeedColors?: boolean;
  theme?: "light" | "dark";
  /** Opt-in zoom/pan (wheel, drag, dblclick, pinch, keyboard). */
  enableZoom?: boolean;
  /** Receives imperative zoom controls; null-able parity with onGetChartControls. */
  onGetMapControls?: (controls: TrackMapControls | null) => void;
}

const TRACK_LAYOUT_THEMES = {
  light: {
    backgroundColor: "#ffffff",
    gridStroke: "#f1f5f9",
    containerClasses: "bg-white border-secondary-200",
    emptyContainerClasses: "bg-secondary-100 border-secondary-200",
    primaryTextClass: "text-secondary-900",
    secondaryTextClass: "text-secondary-500",
    primaryFillClass: "fill-secondary-900",
    trackStroke: "#1f2937",
    pitlaneStroke: "#94a3b8",
    markerStroke: "#ffffff",
    legendTrackClass: "bg-secondary-800",
    legendPitlaneClass: "bg-secondary-400",
  },
  dark: {
    backgroundColor: "#080810",
    gridStroke: "#1a1a2e",
    containerClasses: "bg-[#080810] border-[#1a1a2e]",
    emptyContainerClasses: "bg-[#080810] border-[#1a1a2e]",
    primaryTextClass: "text-slate-200",
    secondaryTextClass: "text-slate-400",
    primaryFillClass: "fill-slate-200",
    trackStroke: "#cbd5e1",
    pitlaneStroke: "#64748b",
    markerStroke: "#080810",
    legendTrackClass: "bg-slate-300",
    legendPitlaneClass: "bg-slate-500",
  },
} as const;

function getSpeedColor(speed: number): string {
  // Speed color gradient: blue (slow) -> green -> yellow -> red (fast)
  // Typical racing speeds: 0-200+ km/h
  if (speed < 30) return "#3b82f6"; // Blue (slow)
  if (speed < 60) return "#10b981"; // Green (moderate)
  if (speed < 100) return "#f59e0b"; // Yellow (fast)
  if (speed < 150) return "#f97316"; // Orange (very fast)
  return "#ef4444"; // Red (extremely fast)
}

export default function TrackLayoutVisualization({
  layoutData,
  className = "w-full h-64",
  showSectors = true,
  showPitlane = true,
  highlightedSector,
  currentPosition,
  currentSpeed,
  positionTrail = [],
  teamPositions = [],
  showSpeedColors = true,
  theme = "light",
  enableZoom = false,
  onGetMapControls,
}: TrackLayoutVisualizationProps) {
  const reactId = useId();
  const { t } = useTranslation(["pages", "common"]);
  const gridPatternId = `track-layout-grid-${reactId.replace(/:/g, "")}`;
  const themeStyles = TRACK_LAYOUT_THEMES[theme];

  const svgRef = useRef<SVGSVGElement | null>(null);
  // Base viewBox constants match svgWidth/svgHeight inside the svgData memo.
  const baseViewBox = useMemo(() => ({ x: 0, y: 0, w: 800, h: 600 }), []);
  const zoom = useTrackMapZoom({
    svgRef,
    base: baseViewBox,
    resetKey: layoutData,
    enabled: enableZoom,
  });
  const { zoomIn, zoomOut, reset, isZoomed, zoomPercent } = zoom;

  const svgData = useMemo(() => {
    const svgWidth = 800;
    const svgHeight = 600;

    if (!layoutData.track_points || layoutData.track_points.length === 0) {
      return null;
    }

    // Calculate bounds for all points (track + pitlane)
    const allPoints = [
      ...layoutData.track_points,
      ...(layoutData.pitlane_points || []),
    ];
    const bounds = calculateBounds(allPoints);

    // Convert track points to SVG coordinates
    const trackSVGPoints = convertGPSToSVG(
      layoutData.track_points,
      bounds,
      svgWidth,
      svgHeight,
    );

    // Convert pitlane points if available
    const pitlaneSVGPoints = layoutData.pitlane_points
      ? convertGPSToSVG(layoutData.pitlane_points, bounds, svgWidth, svgHeight)
      : [];

    // Convert current position if available
    const currentPositionSVG = currentPosition
      ? convertGPSToSVG([currentPosition], bounds, svgWidth, svgHeight)[0]
      : null;

    // Convert position trail if available
    const trailSVGPoints =
      positionTrail.length > 0
        ? convertGPSToSVG(
            positionTrail.map((p) => ({ lat: p.lat, lng: p.lng })),
            bounds,
            svgWidth,
            svgHeight,
          )
        : [];

    // Convert team markers if available
    const teamPositionSVGs = teamPositions.map((p) => ({
      ...p,
      ...convertGPSToSVG(
        [{ lat: p.lat, lng: p.lng }],
        bounds,
        svgWidth,
        svgHeight,
      )[0],
    }));

    return {
      svgWidth,
      svgHeight,
      bounds,
      trackPath: createPathFromPoints(trackSVGPoints),
      pitlanePath: createPathFromPoints(pitlaneSVGPoints),
      trackPoints: trackSVGPoints,
      pitlanePoints: pitlaneSVGPoints,
      currentPosition: currentPositionSVG,
      trailPoints: trailSVGPoints,
      teamPositions: teamPositionSVGs,
    };
  }, [layoutData, currentPosition, positionTrail, teamPositions]);

  // Stable controls identity: svgData recomputes whenever the parent derives
  // fresh position objects (per render in SessionAnalyticsPage), so this
  // effect re-runs on every such render. Reporting a fresh object literal
  // each time made the parent's setMapControls re-render the parent, which
  // re-rendered this map — an infinite commit loop (~180/sec) that starved
  // React Router's back-navigation transition (issue #1151). Memoizing keeps
  // the reference stable while the zoom VALUES are unchanged, so the parent
  // state update bails out and the cycle terminates.
  const mapControls = useMemo(
    () => ({ zoomIn, zoomOut, reset, isZoomed, zoomPercent }),
    [zoomIn, zoomOut, reset, isZoomed, zoomPercent],
  );

  useEffect(() => {
    if (!enableZoom || !onGetMapControls || !svgData) return;
    onGetMapControls(mapControls);
  }, [enableZoom, onGetMapControls, svgData, mapControls]);

  if (!svgData) {
    return (
      <div
 className={`${className} flex items-center justify-center ${themeStyles.emptyContainerClasses} border rounded-lg`}
      >
  <div className={`text-center ${themeStyles.secondaryTextClass}`}>
  <p className="text-sm font-medium">{t("pages:tracks.layout.noTrackLayoutData")}</p>
  <p className="text-xs mt-1">{t("pages:tracks.layout.trackLayoutCoordinatesNotAvailable")}</p>
        </div>
      </div>
    );
  }

  const {
    svgWidth,
    svgHeight,
    trackPath,
    pitlanePath,
    currentPosition: currentPos,
    trailPoints,
    teamPositions: teamMarkers,
  } = svgData;

  return (
    <div
 className={`${className} ${themeStyles.containerClasses} border rounded-lg overflow-hidden`}
    >
 <div className="h-full p-4">
        {/* Track Info Header */}
 <div className="mb-3 text-center">
 <h3 className={`text-sm font-semibold ${themeStyles.primaryTextClass}`}>
            {layoutData.name}
          </h3>
 <div className={`flex justify-center space-x-4 text-xs ${themeStyles.secondaryTextClass} mt-1`}>
            {layoutData.length && (
              <span>{t("pages:tracks.layout.lengthLabel")}{(layoutData.length / 1000).toFixed(2)}km</span>
            )}
            {layoutData.sectors && (
              <span>{t("pages:tracks.layout.sectorsCount", { count: layoutData.sectors.length })}</span>
            )}
          </div>
        </div>

        {/* SVG Track Layout */}
 <div className="flex-1 flex items-center justify-center">
          <svg
            ref={svgRef}
            data-testid="track-layout-svg"
            viewBox={zoom.viewBox}
            tabIndex={enableZoom ? 0 : undefined}
            {...(enableZoom ? zoom.handlers : {})}
            className={`w-full h-full max-h-96${
              enableZoom
                ? " cursor-grab active:cursor-grabbing select-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#3b82f6]"
                : ""
            }`}
            style={{
              maxHeight: "calc(100% - 60px)",
              ...(enableZoom ? { touchAction: "none" as const } : {}),
            }}
          >
            {/* Background grid (optional) */}
            <defs>
              <pattern
                id={gridPatternId}
                width="50"
                height="50"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 50 0 L 0 0 0 50"
                  fill="none"
                  stroke={themeStyles.gridStroke}
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={themeStyles.backgroundColor} />
            <rect width="100%" height="100%" fill={`url(#${gridPatternId})`} />

            {/* Pitlane (drawn first, underneath main track) */}
            {showPitlane && pitlanePath && (
              <path
                d={pitlanePath}
                fill="none"
                stroke={themeStyles.pitlaneStroke}
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="5,5"
                opacity="0.7"
              />
            )}

            {/* Main track */}
            <path
              d={trackPath}
              fill="none"
              stroke={themeStyles.trackStroke}
              strokeWidth="12"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Track center line */}
            <path
              d={trackPath}
              fill="none"
              stroke="#fbbf24"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="10,10"
              opacity="0.8"
            />

            {/* Sector highlighting */}
            {showSectors &&
              layoutData.sectors &&
              layoutData.sectors.map((sector, index) => {
                const isHighlighted = highlightedSector === index;
                const startIndex = sector.start;
                const endIndex =
                  sector.end === 0
                    ? layoutData.track_points.length
                    : sector.end;

                // Create sector path
                const sectorPoints = layoutData.track_points.slice(
                  startIndex,
                  endIndex + 1,
                );
                if (sectorPoints.length > 0) {
                  const sectorSVGPoints = convertGPSToSVG(
                    sectorPoints,
                    svgData.bounds,
                    svgWidth,
                    svgHeight,
                  );
                  const sectorPath = createPathFromPoints(sectorSVGPoints);

                  return (
                    <path
                      key={`sector-${index}`}
                      d={sectorPath}
                      fill="none"
                      stroke={sector.color || "#ef4444"}
                      strokeWidth={isHighlighted ? "16" : "4"}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity={isHighlighted ? 0.9 : 0.6}
                      style={{
                        filter: isHighlighted
                          ? "drop-shadow(0 0 8px rgba(0,0,0,0.3))"
                          : "none",
                      }}
                    />
                  );
                }
                return null;
              })}

            {/* Position trail */}
            {trailPoints.length > 0 && positionTrail.length > 0 && (
              <g>
                {trailPoints.map((point, index) => {
                  const trail = positionTrail[index];
                  const opacity = Math.max(
                    0.2,
                    (index + 1) / trailPoints.length,
                  ); // Fade older points
                  const radius = Math.max(2, 4 * opacity); // Smaller older points
                  const color =
                    showSpeedColors && trail?.speed
                      ? getSpeedColor(trail.speed)
                      : "#3b82f6";

                  return (
                    <circle
                      key={`trail-${index}-${trail?.timestamp || index}`}
                      cx={point.x}
                      cy={point.y}
                      r={radius}
                      fill={color}
                      opacity={opacity}
                    />
                  );
                })}
              </g>
            )}

            {/* Start/Finish line indicator */}
            {svgData.trackPoints.length > 0 && (
              <g>
                <circle
                  cx={svgData.trackPoints[0].x}
                  cy={svgData.trackPoints[0].y}
                  r="8"
                  fill="#22c55e"
                  stroke={themeStyles.markerStroke}
                  strokeWidth="2"
                />
                <text
                  x={svgData.trackPoints[0].x}
                  y={svgData.trackPoints[0].y - 15}
                  textAnchor="middle"
                  className={`text-xs font-bold ${themeStyles.primaryFillClass}`}
                >
                  {t("pages:tracks.layout.startFinishShort")}
                </text>
              </g>
            )}

            {/* Current position indicator (single-vehicle analytics view) */}
            {currentPos && teamMarkers.length === 0 && (
              <g data-testid="track-current-position">
                {/* Outer pulsing ring */}
                <circle
                  cx={currentPos.x}
                  cy={currentPos.y}
                  r="12"
                  fill="none"
                  stroke={
                    showSpeedColors && currentSpeed
                      ? getSpeedColor(currentSpeed)
                      : "#3b82f6"
                  }
                  strokeWidth="2"
                  opacity="0.6"
                >
                  <animate
                    attributeName="r"
                    values="8;16;8"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.8;0.2;0.8"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>

                {/* Main position dot */}
                <circle
                  cx={currentPos.x}
                  cy={currentPos.y}
                  r="6"
                  fill={
                    showSpeedColors && currentSpeed
                      ? getSpeedColor(currentSpeed)
                      : "#3b82f6"
                  }
                  stroke={themeStyles.markerStroke}
                  strokeWidth="2"
                />

                {/* Speed indicator text (if available) */}
                {currentSpeed !== undefined && (
                  <text
                    x={currentPos.x}
                    y={currentPos.y - 20}
                    textAnchor="middle"
                    className={`text-xs font-semibold ${themeStyles.primaryFillClass}`}
                    style={{ fontSize: "10px" }}
                  >
                    {Math.round(currentSpeed)} km/h
                  </text>
                )}
              </g>
            )}

            {/* Team live markers (team live map) — one per member */}
            <TeamPositionMarkers
              markers={teamMarkers}
              theme={theme}
              markerStroke={themeStyles.markerStroke}
            />
          </svg>
        </div>

        {/* Track Statistics */}
 <div className={`mt-3 flex justify-center space-x-6 text-xs ${themeStyles.secondaryTextClass}`}>
 <div className="flex items-center space-x-1">
  <div className={`w-3 h-0.5 ${themeStyles.legendTrackClass}`}></div>
            <span>{t("pages:tracks.layout.trackLabel")}</span>
          </div>
          {showPitlane && layoutData.pitlane_points && (
  <div className="flex items-center space-x-1">
  <div className={`w-3 h-0.5 ${themeStyles.legendPitlaneClass} border-dashed`}></div>
               <span>{t("pages:tracks.layout.pitlaneLabel")}</span>
             </div>
           )}
           {showSectors && layoutData.sectors && (
  <div className="flex items-center space-x-1">
  <div className="flex space-x-1">
                 {layoutData.sectors.slice(0, 3).map((sector, index) => (
                   <div
                     key={index}
  className="w-2 h-2 rounded-full"
                     style={{ backgroundColor: sector.color }}
                   />
                 ))}
               </div>
               <span>{t("pages:tracks.layout.sectorsLabel")}</span>
             </div>
           )}
        </div>
      </div>
    </div>
  );
}
